/**
 * Trusscore Estimator Presets
 *
 * Pre-configured room sizes and design configurations
 * matching the existing Trusscore Project Planner.
 */

import { RoomPreset, DesignPreset, ProductColor, PanelOrientation } from '../types';

// ============================================================================
// ROOM SIZE PRESETS
// ============================================================================

/**
 * Standard room size presets.
 * These match the exact presets from the legacy Trusscore Project Planner.
 */
export const ROOM_PRESETS: RoomPreset[] = [
  {
    id: 'small-shed',
    name: "6' × 10' × 8'",
    length: 6,
    width: 10,
    height: 8,
  },
  {
    id: 'single-car',
    name: "12' × 20' × 10'",
    length: 12,
    width: 20,
    height: 10,
  },
  {
    id: 'double-car',
    name: "20' × 20' × 10'",
    length: 20,
    width: 20,
    height: 10,
  },
  {
    id: 'large-garage',
    name: "18' × 24' × 8'",
    length: 18,
    width: 24,
    height: 8,
  },
  {
    id: 'workshop',
    name: "20' × 30' × 10'",
    length: 20,
    width: 30,
    height: 10,
  },
];

// ============================================================================
// DESIGN CONFIGURATION PRESETS
// ============================================================================

/**
 * Pre-designed room configurations.
 * These match the 5 design presets in the Trusscore Project Planner:
 * - Ultimate Modern
 * - Ultimate Classic
 * - Premium Classic
 * - Essential Modern
 * - Essential Classic
 */
export const DESIGN_PRESETS: DesignPreset[] = [
  {
    id: 'ultimate-modern',
    name: 'Ultimate Modern',
    description: 'Horizontal gray boards with 50% white SlatWall coverage',
    wallColor: 'Gray',
    wallOrientation: 'Horizontal',
    slatWallCoverage: 50,
    slatWallColor: 'White',
    includeCeiling: true,
    ceilingColor: 'White',
  },
  {
    id: 'ultimate-classic',
    name: 'Ultimate Classic',
    description: 'Vertical white boards with 50% white SlatWall coverage',
    wallColor: 'White',
    wallOrientation: 'Vertical',
    slatWallCoverage: 50,
    slatWallColor: 'White',
    includeCeiling: true,
    ceilingColor: 'White',
  },
  {
    id: 'premium-classic',
    name: 'Premium Classic',
    description: 'Vertical white boards with 25% white SlatWall coverage',
    wallColor: 'White',
    wallOrientation: 'Vertical',
    slatWallCoverage: 25,
    slatWallColor: 'White',
    includeCeiling: true,
    ceilingColor: 'White',
  },
  {
    id: 'essential-modern',
    name: 'Essential Modern',
    description: 'Horizontal gray boards with no SlatWall',
    wallColor: 'Gray',
    wallOrientation: 'Horizontal',
    slatWallCoverage: 0,
    slatWallColor: 'Gray', // Default, won't be used
    includeCeiling: true,
    ceilingColor: 'Gray',
  },
  {
    id: 'essential-classic',
    name: 'Essential Classic',
    description: 'Vertical white boards with no SlatWall',
    wallColor: 'White',
    wallOrientation: 'Vertical',
    slatWallCoverage: 0,
    slatWallColor: 'White', // Default, won't be used
    includeCeiling: true,
    ceilingColor: 'White',
  },
];

// ============================================================================
// SLATWALL COVERAGE CALCULATIONS
// ============================================================================

/**
 * Calculate SlatWall configuration based on coverage percentage and wall height.
 *
 * Design Preset SlatWall Positions:
 * - 50% coverage: Starts at 50% of wall height (middle) and extends to the top
 * - 25% coverage: Starts at 50% of wall height and extends to 75% (middle quarter)
 *
 * @param coveragePercent - 0, 25, or 50 percent SlatWall coverage
 * @param wallHeight - Total wall height in feet
 * @returns SlatWall start height and number of panel rows
 */
export function calculateSlatWallFromCoverage(
  coveragePercent: 0 | 25 | 50,
  wallHeight: number
): { startHeight: number; numPanelRows: number } {
  if (coveragePercent === 0) {
    return { startHeight: wallHeight, numPanelRows: 0 };
  }

  // Each SlatWall row is 6 inches (0.5 feet)
  const SLATWALL_ROW_HEIGHT = 0.5; // feet

  let startHeight: number;
  let targetSlatWallHeight: number;

  if (coveragePercent === 50) {
    // 50% coverage: starts at middle of wall (50%) and goes to top (100%)
    // SlatWall covers the top half of the wall
    startHeight = wallHeight * 0.5; // Start at 50% of wall height
    targetSlatWallHeight = wallHeight * 0.5; // Covers 50% of wall (to the top)
  } else {
    // 25% coverage: starts at 50% of wall height and extends to 75%
    // SlatWall covers the third quarter of the wall (50% to 75%)
    startHeight = wallHeight * 0.5; // Start at 50% of wall height
    targetSlatWallHeight = wallHeight * 0.25; // Covers 25% of wall (to 75% height)
  }

  // Calculate number of rows (round to nearest whole row)
  const numPanelRows = Math.round(targetSlatWallHeight / SLATWALL_ROW_HEIGHT);

  return { startHeight, numPanelRows };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a room preset by ID.
 */
export function getRoomPresetById(id: string): RoomPreset | undefined {
  return ROOM_PRESETS.find((preset) => preset.id === id);
}

/**
 * Get a design preset by ID.
 */
export function getDesignPresetById(id: string): DesignPreset | undefined {
  return DESIGN_PRESETS.find((preset) => preset.id === id);
}

/**
 * Apply a design preset to get full wall configuration.
 */
export function applyDesignPreset(
  preset: DesignPreset,
  wallHeight: number
): {
  wallColor: ProductColor;
  wallOrientation: PanelOrientation;
  slatWall: {
    enabled: boolean;
    startHeight: number;
    numPanelRows: number;
    color: ProductColor;
    useCapTrim: boolean;
  };
  includeCeiling: boolean;
  ceilingColor: ProductColor;
} {
  const slatWallConfig = calculateSlatWallFromCoverage(preset.slatWallCoverage, wallHeight);

  return {
    wallColor: preset.wallColor,
    wallOrientation: preset.wallOrientation,
    slatWall: {
      enabled: preset.slatWallCoverage > 0,
      startHeight: slatWallConfig.startHeight,
      numPanelRows: slatWallConfig.numPanelRows,
      color: preset.slatWallColor,
      useCapTrim: preset.slatWallCoverage > 0, // Use cap trim if SlatWall is enabled
    },
    includeCeiling: preset.includeCeiling,
    ceilingColor: preset.ceilingColor,
  };
}

// ============================================================================
// APPLICATION/SPACE TYPE OPTIONS
// ============================================================================

/**
 * The space types — THE list; everything else derives from it. To add one:
 *   1. an entry here (its icon a lucide name listed in data/spaceIcons.ts);
 *   2. its staging kit in calculations/staging/kits.ts STAGING_KITS (what
 *      Stage furnishes it with, and `feature`: the props its 360 faces);
 *   3. its name in each locale's wizard.json `spaceTypes` (the wizard's
 *      labels are translated; the designer's and admin's come from here).
 * `legacy` is the Project Planner visualizer's slug for it ('generic' when
 * it has none). The analytics page labels an unknown slug by itself.
 * Where each is OFFERED (which wizard modes, Advanced) is the admin's, not
 * the code's: Admin → Settings → Space types (data/spaceAccess.ts).
 */
export const SPACE_TYPES = [
  { id: 'garage', label: 'Garage', icon: 'Car', legacy: 'garage' },
  { id: 'living-space', label: 'Living Space', icon: 'Home', legacy: 'livingspace' },
  { id: 'basement', label: 'Basement', icon: 'Building2', legacy: 'basement' },
  { id: 'laundry-room', label: 'Laundry Room', icon: 'WashingMachine', legacy: 'laundryroom' },
  { id: 'workshop', label: 'Workshop', icon: 'Hammer', legacy: 'garage' },
  { id: 'shed', label: 'Shed', icon: 'Warehouse', legacy: 'garage' },
  { id: 'washroom', label: 'Bathroom', icon: 'Bath', legacy: 'bathroom' },
  { id: 'commercial-kitchen', label: 'Commercial Kitchen', icon: 'ChefHat', legacy: 'generic' },
  { id: 'other', label: 'Other', icon: 'LayoutGrid', legacy: 'generic' },
] as const;

/** Every space type's id. */
export const SPACE_TYPE_IDS: readonly string[] = SPACE_TYPES.map((t) => t.id);

/** A space type's label (its id when unknown). */
export const spaceTypeLabel = (id: string): string => SPACE_TYPES.find((t) => t.id === id)?.label ?? id;

// Space types that support garage doors
export const GARAGE_DOOR_SPACE_TYPES = ['garage', 'shed', 'workshop', 'other'] as const;

export type SpaceType = (typeof SPACE_TYPES)[number]['id'];

/**
 * Standard garage door sizes.
 *
 * `label` is the English display string and stays as-is because analytics
 * reads it (trackPresetSelected) — translating it would fragment the same
 * preset across three names in reporting. The UI renders `sizeClass` through
 * i18n instead, interpolating width/height.
 */
export const GARAGE_DOOR_PRESETS = [
  { width: 8, height: 7, label: "8' × 7' (Single)", sizeClass: 'single' },
  { width: 9, height: 7, label: "9' × 7' (Single)", sizeClass: 'single' },
  { width: 10, height: 7, label: "10' × 7' (Single)", sizeClass: 'single' },
  { width: 16, height: 7, label: "16' × 7' (Double)", sizeClass: 'double' },
  { width: 16, height: 8, label: "16' × 8' (Double)", sizeClass: 'double' },
  { width: 18, height: 7, label: "18' × 7' (Double Wide)", sizeClass: 'doubleWide' },
] as const;

export default {
  ROOM_PRESETS,
  DESIGN_PRESETS,
  SPACE_TYPES,
  GARAGE_DOOR_PRESETS,
};
